import React from 'react'
import { DatePicker } from '@fluentui/react-datepicker-compat'
import { Field } from './Field'
import { Control, Controller } from 'react-hook-form'
import { t } from '../../../i18n/i18n'
import { formatDate } from '../../../helpers/stringHelper'
import dayjs from 'dayjs'
import { ConditionalTooltip } from '../other/ConditionalTooltip'
type ControlledDatePickerProps = {
   name: any
   control: Control<any>
   label: string
   isDisabled?: boolean
   required?: boolean
   getValues?: (name: string) => any
   compareWith?: string
   defaultDate?: string
   oneDayActivity?: boolean
   showTooltip?: boolean
   tooltipMsg?: string
}
export const ControlledDatePicker = ({
   name,
   control,
   label,
   isDisabled,
   required,
   getValues,
   compareWith,
   defaultDate,
   oneDayActivity,
   showTooltip,
   tooltipMsg,
}: ControlledDatePickerProps) => {
   const MIN_DATE = new Date(1900, 0, 1) // 1900-01-01
   const MAX_DATE = new Date(2999, 12, 30) // 2999-12-31
   const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

   const inRange = (d: Date) => d >= MIN_DATE && d <= MAX_DATE
   const isValid = (d: Date) => !Number.isNaN(d.getTime())

   const parseDateFromString = (text: string): Date | null => {
      const s = text.trim()
      if (!ISO_DATE_RE.test(s)) return null
      const [y, m, d] = s.split('-').map(Number)
      const dt = new Date(y, m - 1, d)
      if (!isValid(dt) || dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null
      if (!inRange(dt)) return null
      return dt
   }

   return (
      <Controller
         name={name}
         control={control}
         rules={{
            required: required ? t('RequiredField') : false,
            validate: (endDate) => {
               if (compareWith && getValues) {
                  const startDate = getValues(compareWith)
                  if (oneDayActivity) {
                     if (!endDate) return true
                     return dayjs.utc(endDate).isSameOrAfter(dayjs.utc(startDate)) || t('EndDateMustBeAfterOrTheSameAsStartDate')
                  } else {
                     if (!startDate || !endDate) return true
                     return dayjs.utc(endDate).isAfter(dayjs.utc(startDate)) || t('EndDateMustBeAfterStartDate')
                  }
               }
               return true
            },
         }}
         render={({ field: { value, onChange }, fieldState: { error }, formState: { disabled } }) => (
            <Field label={label} required={required ?? false} validationMessage={error ? error.message : null} validationState={error ? 'error' : null}>
               <ConditionalTooltip show={showTooltip} content={tooltipMsg}>
                  <DatePicker
                     minDate={required ? MIN_DATE : undefined}
                     maxDate={required ? MAX_DATE : undefined}
                     parseDateFromString={parseDateFromString}
                     strings={{
                        days: [t('Monday'), t('Tuesday'), t('Wednesday'), t('Thursday'), t('Friday'), t('Saturday'), t('Sunday')],
                        goToToday: t('GoToToday'),
                        months: [
                           t('January'),
                           t('February'),
                           t('March'),
                           t('April'),
                           t('May'),
                           t('June'),
                           t('July'),
                           t('August'),
                           t('September'),
                           t('October'),
                           t('November'),
                           t('December'),
                        ],
                        shortDays: [
                           t('SundayShort'),
                           t('MondayShort'),
                           t('TuesdayShort'),
                           t('WednesdayShort'),
                           t('ThursdayShort'),
                           t('FridayShort'),
                           t('SaturdayShort'),
                        ],
                        shortMonths: [t('Jan'), t('Feb'), t('Mar'), t('Apr'), t('May'), t('Jun'), t('Jul'), t('Aug'), t('Sep'), t('Oct'), t('Nov'), t('Dec')],
                     }}
                     style={{ width: '100%' }}
                     required={required ?? false}
                     today={new Date()}
                     allowTextInput
                     firstDayOfWeek={1}
                     value={defaultDate ? dayjs.utc(defaultDate).toDate() : value ? dayjs.utc(value).toDate() : null}
                     onSelectDate={(date) => {
                        if (!date) {
                           onChange(required ? new Date() : null)
                        } else {
                           onChange(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
                        }
                     }}
                     disabled={disabled || isDisabled ? true : false}
                     formatDate={(date) => formatDate(date)}
                     showWeekNumbers
                  />
               </ConditionalTooltip>
            </Field>
         )}
      />
   )
}
